// Pure rights/lifecycle/grid/event helpers for the RTP Planner feature.
// Mirrors docs/superpowers/specs/2026-08-29-rtp-planner-api-reference.md (§9 permission matrix,
// §5 event catalog, §10 lifecycle rules). No React/Redux/axios imports.
import dayjs from "dayjs";
import type {
	RtpBlockContent,
	RtpBlockTemplate,
	RtpCriterion,
	RtpCriterionDetails,
	RtpEvent,
	RtpNote,
	RtpPhase,
	RtpPlanDetail,
	RtpPlanListItem,
	RtpPlanStatus,
	RtpRights,
} from "@components/Settings/RtpPlanner/types";

// Blocks no longer have a mode. Old events in the event log keep the stored mode value
// (general / gym / field), and this map keeps those events readable.
const LEGACY_BLOCK_MODE_LABELS: Record<string, string> = {
	general: "General",
	gym: "Detailed",
	field: "Target",
};

export function getRtpErrorMessage(e: unknown): string {
	const data = (e as { response?: { data?: unknown } })?.response?.data;
	if (typeof data === "string" && data.length > 0) return data;
	return "Something went wrong — please try again.";
}

export function getRtpRights(
	plan: RtpPlanDetail,
	uuid: string | null | undefined
): RtpRights {
	const flags = uuid ? plan.relevantStaff[uuid] : undefined;
	const isManager = Boolean(flags?.CanManagePlan);
	return {
		isManager,
		canCreateDayPlan: isManager || Boolean(flags?.CanCreateDayPlan),
		canCreateExitCriteria: isManager || Boolean(flags?.CanCreateExitCriteria),
	};
}

export function isPlanReadOnly(plan: RtpPlanDetail): boolean {
	return plan.status === "completed" || plan.status === "cancelled";
}

export function isPhaseLocked(phase: RtpPhase): boolean {
	return phase.completedAt != null;
}

export function isActivePhase(plan: RtpPlanDetail, phase: RtpPhase): boolean {
	return plan.activePhaseId === phase.id;
}

export function canSignNow(plan: RtpPlanDetail, phase: RtpPhase): boolean {
	return plan.status === "active" && isActivePhase(plan, phase);
}

export function canSignCriterion(
	plan: RtpPlanDetail,
	phase: RtpPhase,
	criterion: RtpCriterion,
	uuid: string | null | undefined
): boolean {
	if (!uuid) return false;
	return (
		canSignNow(plan, phase) &&
		criterion.signedOffBy == null &&
		criterion.allowedSigners.includes(uuid)
	);
}

export function canEditCriterion(
	plan: RtpPlanDetail,
	criterion: RtpCriterion,
	uuid: string | null | undefined
): boolean {
	if (!uuid) return false;
	const rights = getRtpRights(plan, uuid);
	if (rights.isManager) return true;
	if (criterion.createdBy === uuid && rights.canCreateExitCriteria) return true;
	return criterion.allowedSigners.includes(uuid);
}

// NOT a mere signer — deleting requires manager or the creator with the flag (§9).
export function canDeleteCriterion(
	plan: RtpPlanDetail,
	criterion: RtpCriterion,
	uuid: string | null | undefined
): boolean {
	if (!uuid) return false;
	const rights = getRtpRights(plan, uuid);
	if (rights.isManager) return true;
	return criterion.createdBy === uuid && rights.canCreateExitCriteria;
}

// "The signer" = whoever actually signed (signedOffBy), not any listed signer.
export function canAmendSignOffNotes(
	phase: RtpPhase,
	criterion: RtpCriterion,
	uuid: string | null | undefined
): boolean {
	if (!uuid) return false;
	return criterion.signedOffBy === uuid && !isPhaseLocked(phase);
}

export function canEditNote(
	note: RtpNote,
	uuid: string | null | undefined
): boolean {
	return Boolean(uuid) && note.createdBy === uuid;
}

export function getUserName(
	plan: RtpPlanDetail,
	uuid: string | null | undefined
): string {
	if (!uuid) return "Unknown user";
	return plan.users[uuid]?.name ?? "Unknown user";
}

export const FORMER_STAFF = "Former staff";
export const WHISTLE_AUTHOR = "Whistle Performance";

export function getBlockEntryCreator(entry: RtpBlockTemplate): string {
	if (entry.source === "whistle") return WHISTLE_AUTHOR;
	return entry.createdBy?.name || FORMER_STAFF;
}

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

// "1 exercise · 1 target", or "Description only" when the block has neither.
export function summarizeBlockContent({ exercises, targets }: RtpBlockContent): string {
	const parts = [
		...(exercises.length > 0 ? [plural(exercises.length, "exercise")] : []),
		...(targets.length > 0 ? [plural(targets.length, "target")] : []),
	];
	return parts.length > 0 ? parts.join(" · ") : "Description only";
}

// True when both contents hold the same data. The key order of a row and a blank optional field
// (missing or "") do not count. The order of the rows counts.
export function sameBlockContent(a: RtpBlockContent, b: RtpBlockContent): boolean {
	const canonicalRow = (row: object) =>
		Object.fromEntries(
			Object.entries(row)
				.filter(([, value]) => value != null && value !== "")
				.sort(([x], [y]) => (x < y ? -1 : 1))
		);
	const canonical = (c: RtpBlockContent) =>
		JSON.stringify([c.description, c.exercises.map(canonicalRow), c.targets.map(canonicalRow)]);
	return canonical(a) === canonical(b);
}

// The same limit as the backend: a longer library title gets a 400.
export const BLOCK_LIBRARY_TITLE_MAX_LENGTH = 150;

// The title of a copy of a library entry: "Copy of X", then "Copy of X (2)", "Copy of X (3)", and so
// on while the title is taken. A title clash ignores the case and the outer spaces. A long title
// is cut, so the copy keeps within the limit.
export function nextCopyTitle(title: string, takenTitles: string[]): string {
	const taken = new Set(takenTitles.map((item) => item.trim().toLowerCase()));
	const prefix = "Copy of ";
	for (let n = 1; ; n++) {
		const suffix = n === 1 ? "" : ` (${n})`;
		const room = BLOCK_LIBRARY_TITLE_MAX_LENGTH - prefix.length - suffix.length;
		const candidate = `${prefix}${title.trim().slice(0, room).trimEnd()}${suffix}`;
		if (!taken.has(candidate.toLowerCase())) return candidate;
	}
}

// The usage chips of a library card: "Used 3 times in 2 plans" and "Last used today / 1 day ago /
// 4 days ago". An entry that no block uses has no chips. The age counts calendar days in the
// browser time zone, so a use at 23:50 yesterday is "1 day ago" at 00:30.
export function blockEntryUsageChips(
	{ timesUsed, plansUsed, lastUsedAt }: Pick<RtpBlockTemplate, "timesUsed" | "plansUsed" | "lastUsedAt">,
	now: dayjs.Dayjs = dayjs()
): string[] {
	if (!timesUsed) return [];
	const chips = [`Used ${plural(timesUsed, "time")} in ${plural(plansUsed, "plan")}`];
	if (lastUsedAt) {
		const days = Math.max(now.startOf("day").diff(dayjs(lastUsedAt).startOf("day"), "day"), 0);
		chips.push(days === 0 ? "Last used today" : `Last used ${plural(days, "day")} ago`);
	}
	return chips;
}

const BLOCK_TITLE_MAX_LENGTH = 40;

// The title that the star popover offers for a new library entry: the first line of the
// description, else the first exercise ("Nordic curl, 3 x 6"), else the first target. Empty when
// the content has none of them.
export function suggestBlockTitle(
	{ description, exercises, targets }: RtpBlockContent,
	metricLabel: (key: string) => string
): string {
	const line = description.trim().split("\n")[0].trim();
	if (line) return line.length > BLOCK_TITLE_MAX_LENGTH ? `${line.slice(0, BLOCK_TITLE_MAX_LENGTH).trim()}…` : line;
	const exercise = exercises.find((item) => item.name.trim());
	if (exercise) {
		const name = exercise.name.trim();
		return exercise.sets && exercise.reps ? `${name}, ${exercise.sets} x ${exercise.reps}` : name;
	}
	const target = targets[0];
	return target ? [metricLabel(target.metricKey), target.target].filter(Boolean).join(" ") : "";
}

// Library search: the query is a part of the title, the description, an exercise name, a metric
// key or label, or the creator name. A blank query matches every entry.
export function blockEntryMatches(
	entry: RtpBlockTemplate,
	query: string,
	metricLabel: (key: string) => string
): boolean {
	const q = query.trim().toLowerCase();
	if (!q) return true;
	const { description, exercises, targets } = entry.content;
	return [
		entry.title,
		description,
		getBlockEntryCreator(entry),
		...exercises.map((exercise) => exercise.name),
		...targets.flatMap((target) => [target.metricKey, metricLabel(target.metricKey)]),
	].some((text) => text.toLowerCase().includes(q));
}

// Day X of Y for an active plan: X = days since start (inclusive), Y = sum of phase durations
// (real for completed phases, planned for the rest). Null when the plan isn't running.
export function planDayProgress(plan: RtpPlanDetail): { dayNumber: number; totalDays: number } | null {
	if (plan.status !== "active" || !plan.startDate) return null;
	const totalDays = plan.phases.reduce((sum, phase) => sum + phaseDurationDays(phase), 0);
	if (totalDays <= 0) return null;
	return { dayNumber: dayjs().diff(dayjs(plan.startDate), "day") + 1, totalDays };
}

// "Aug 25 – Sep 7 (expected) · 14 days" — real dates once completed, projected otherwise.
// `markExpected: false` drops the "(expected)" note (the Schedule phase picker).
type RtpPhaseDates = Pick<RtpPhase, "startDate" | "completedAt" | "projectedEndDate" | "plannedDurationDays">;

// The calendar day a phase was completed. completedAt is a UTC timestamp, and the backend takes
// its UTC date as the next phase's start date. dayjs(completedAt) would use the browser's time
// zone instead, so a phase completed on a US evening would end a day before the next one starts.
export function completedDay(completedAt: string): string {
	return completedAt.slice(0, 10);
}

export function phaseDateMeta(phase: RtpPhaseDates, markExpected = true): string {
	const parts: string[] = [];
	if (phase.startDate) {
		const start = dayjs(phase.startDate).format("MMM D");
		const end = phase.completedAt ? completedDay(phase.completedAt) : phase.projectedEndDate;
		const endLabel = end ? dayjs(end).format("MMM D") : null;
		const range = endLabel && endLabel !== start ? `${start} – ${endLabel}` : start;
		const expected = markExpected && !phase.completedAt ? " (expected)" : "";
		parts.push(end ? `${range}${expected}` : `Starts ${start}`);
	}
	const days = phaseDurationDays(phase);
	parts.push(`${days} day${days === 1 ? "" : "s"}`);
	return parts.join(" · ");
}

// Real elapsed days for completed phases (start → completedAt inclusive), planned duration otherwise.
export function phaseDurationDays(phase: RtpPhaseDates): number {
	if (phase.startDate && phase.completedAt) {
		return dayjs(completedDay(phase.completedAt)).diff(dayjs(phase.startDate), "day") + 1;
	}
	return phase.plannedDurationDays;
}

export interface RtpPhaseRange {
	phase: RtpPhase;
	start: dayjs.Dayjs;
	end: dayjs.Dayjs;
}

// Non-overlapping inclusive [start, end] per dated phase, in order. Consecutive phases share
// their boundary day server-side (completedAt == next phase's startDate), so each phase ends
// the day before the next starts; the last runs its real/planned duration.
export function phaseDateRanges(plan: RtpPlanDetail): RtpPhaseRange[] {
	const phases = [...plan.phases].sort((a, b) => a.order - b.order).filter((p) => p.startDate);
	return phases.map((phase, i) => {
		const start = dayjs(phase.startDate).startOf("day");
		const next = phases[i + 1];
		const end = next
			? dayjs(next.startDate).startOf("day").subtract(1, "day")
			: start.add(phaseDurationDays(phase) - 1, "day");
		return { phase, start, end: end.isBefore(start) ? start : end };
	});
}

// The days inside [from, to] on which a metric exit criteria's threshold was met (daily value vs
// threshold, honoring the comparison), oldest first. Empty for event criteria or no threshold.
export function metricCriterionMetDays(
	details: RtpCriterionDetails,
	dailyValues: { date: string; value: number | null }[],
	from: dayjs.Dayjs,
	to: dayjs.Dayjs
): { date: string; value: number }[] {
	const { threshold, comparison } = details;
	if (threshold == null) return [];
	return dailyValues
		.filter((d): d is { date: string; value: number } => d.value != null)
		.filter((d) => {
			const day = dayjs(d.date).startOf("day");
			return !day.isBefore(from) && !day.isAfter(to);
		})
		.filter((d) => (comparison === "<=" ? d.value <= threshold : d.value >= threshold))
		.sort((a, b) => a.date.localeCompare(b.date));
}

export function allCriteriaSigned(phase: RtpPhase): boolean {
	return phase.criteria.every((criterion) => criterion.signedOffBy != null);
}

export function isFinalPhase(plan: RtpPlanDetail, phase: RtpPhase): boolean {
	const highestOrder = plan.phases.reduce(
		(max, p) => Math.max(max, p.order),
		-Infinity
	);
	return phase.order === highestOrder;
}

export function getDayCount(phase: RtpPhase): number {
	const highestBlockDay = phase.blocks.reduce(
		(max, block) => Math.max(max, block.dayIndex),
		0
	);
	return Math.max(phase.plannedDurationDays, highestBlockDay, 1);
}

export function getInitials(name: string): string {
	return (
		name
			.split(/\s+/)
			.filter(Boolean)
			.slice(0, 2)
			.map((word) => word[0].toUpperCase())
			.join("") || "?"
	);
}

function pickString(obj: unknown, ...path: string[]): string | undefined {
	let cur: unknown = obj;
	for (const key of path) {
		if (!cur || typeof cur !== "object") return undefined;
		cur = (cur as Record<string, unknown>)[key];
	}
	return typeof cur === "string" ? cur : undefined;
}

function pickNumber(obj: unknown, ...path: string[]): number | undefined {
	let cur: unknown = obj;
	for (const key of path) {
		if (!cur || typeof cur !== "object") return undefined;
		cur = (cur as Record<string, unknown>)[key];
	}
	return typeof cur === "number" ? cur : undefined;
}

// Snapshots are taken from `after` when present (create/update), else `before` (delete).
function pickEither(event: RtpEvent, ...path: string[]): string | undefined {
	return (
		pickString(event.payload.after, ...path) ??
		pickString(event.payload.before, ...path)
	);
}

function pickNumberEither(event: RtpEvent, ...path: string[]): number | undefined {
	return (
		pickNumber(event.payload.after, ...path) ??
		pickNumber(event.payload.before, ...path)
	);
}

const asNumber = (value: unknown): number | undefined =>
	typeof value === "number" ? value : undefined;

type RtpEventDescriber = (event: RtpEvent) => string;

const LIBRARY_LINK_KEYS = new Set(["templateId", "inLibrary"]);

// The star on a block: the block is in the library after the event, and the snapshots differ
// only in the library link.
function isLibrarySaveEvent({ payload }: RtpEvent): boolean {
	const before = flattenSnapshot(payload.before);
	const after = flattenSnapshot(payload.after);
	if (before.inLibrary === true || after.inLibrary !== true) return false;
	return Object.keys({ ...before, ...after }).every(
		(key) => LIBRARY_LINK_KEYS.has(key) || JSON.stringify(before[key]) === JSON.stringify(after[key])
	);
}

// Keyed by the §5 event-type catalog (20 types). Partial so an eventType the backend adds later
// falls back to the raw string below instead of crashing.
const RTP_EVENT_DESCRIBERS: Partial<Record<RtpEvent["eventType"], RtpEventDescriber>> = {
	plan_created: (e) => {
		const name = pickEither(e, "name");
		return name ? `created the plan "${name}"` : "created the plan";
	},
	plan_updated: () => "updated the plan details",
	plan_started: () => "started the plan",
	plan_completed: () => "completed the plan",
	plan_cancelled: () => "cancelled the plan",
	schedule_exported: (e) => {
		const recipients = Array.isArray(e.payload.recipientUuids) ? e.payload.recipientUuids.length : 0;
		if (e.payload.delivery !== "email") return "downloaded the schedule";
		return `emailed the schedule to ${recipients} staff`;
	},

	phase_added: (e) => {
		const name = pickEither(e, "name");
		return name ? `added the phase "${name}"` : "added a phase";
	},
	phase_updated: (e) => {
		const name = pickEither(e, "name");
		return name ? `updated the phase "${name}"` : "updated a phase";
	},
	phase_deleted: (e) => {
		const name = pickEither(e, "name");
		return name ? `deleted the phase "${name}"` : "deleted a phase";
	},
	phase_completed: (e) => {
		const name = pickEither(e, "name");
		return name ? `completed the phase "${name}"` : "completed a phase";
	},

	criterion_added: (e) => {
		const desc = pickEither(e, "details", "description");
		return desc ? `added exit criteria "${desc}"` : "added exit criteria";
	},
	criterion_updated: (e) => {
		const desc = pickEither(e, "details", "description");
		// The sign-off-cleared state is shown via the Tag in EventLogTab, not duplicated here.
		return desc ? `updated exit criteria "${desc}"` : "updated exit criteria";
	},
	criterion_deleted: (e) => {
		const desc = pickEither(e, "details", "description");
		return desc ? `deleted exit criteria "${desc}"` : "deleted exit criteria";
	},
	criterion_signed_off: (e) => {
		const desc = pickEither(e, "details", "description");
		return desc ? `signed off "${desc}"` : "signed off exit criteria";
	},

	block_added: (e) => {
		// A block that comes from a library entry has its templateId in the snapshot.
		const block = pickEither(e, "templateId") ? "a block from the library" : "a block";
		const batchDays = e.payload.batchDays;
		if (Array.isArray(batchDays) && batchDays.length > 0) {
			return `added ${block} to days ${batchDays.join(", ")}`;
		}
		const dayIndex = pickNumberEither(e, "dayIndex");
		return dayIndex != null ? `added ${block} for day ${dayIndex}` : `added ${block}`;
	},
	block_updated: (e) => {
		// A drag and drop between days is a dayIndex-only PATCH.
		const fromDay = pickNumber(e.payload.before, "dayIndex");
		const toDay = pickNumber(e.payload.after, "dayIndex");
		if (fromDay != null && toDay != null && fromDay !== toDay) {
			return `moved a block from day ${fromDay} to day ${toDay}`;
		}
		const dayIndex = pickNumberEither(e, "dayIndex");
		if (isLibrarySaveEvent(e)) {
			return dayIndex != null ? `saved a block to the library for day ${dayIndex}` : "saved a block to the library";
		}
		return dayIndex != null ? `updated the block for day ${dayIndex}` : "updated a block";
	},
	block_deleted: (e) => {
		const dayIndex = pickNumberEither(e, "dayIndex");
		return dayIndex != null ? `deleted the block for day ${dayIndex}` : "deleted a block";
	},
	blocks_shifted: (e) => {
		const count = asNumber(e.payload.count);
		const fromDay = asNumber(e.payload.fromDay);
		const shift = asNumber(e.payload.shift);
		const countText = count != null ? `${count} block${count === 1 ? "" : "s"}` : "blocks";
		const fromText = fromDay != null ? ` from day ${fromDay}` : "";
		const shiftText =
			shift != null
				? ` by ${shift > 0 ? "+" : ""}${shift} day${Math.abs(shift) === 1 ? "" : "s"}`
				: "";
		return `shifted ${countText}${fromText}${shiftText}`;
	},

	note_added: () => "added a note",
	note_updated: () => "updated a note",
	note_deleted: () => "deleted a note",
};

// Actor-less on purpose — EventLogTab renders the actor name bold in front of it.
export function describeRtpEventSentence(event: RtpEvent): string {
	const describer = RTP_EVENT_DESCRIBERS[event.eventType];
	return describer ? describer(event) : event.eventType;
}

const RTP_CHANGE_LABELS: Record<string, string> = {
	name: "Name",
	goal: "Goal",
	plannedDurationDays: "Planned duration",
	description: "Description",
	metricSource: "Source",
	metricKey: "Metric",
	comparison: "Comparison",
	threshold: "Threshold",
	startDate: "Start date",
	text: "Note",
	dayIndex: "Day",
	mode: "Mode",
	signedOffNotes: "Notes",
};

const truncateText = (value: string, max = 80): string =>
	value.length > max ? `${value.slice(0, max - 1)}…` : value;

function formatChangeValue(key: string, value: unknown): string | null {
	if (value == null || value === "") return "—";
	if (key === "mode" && typeof value === "string") {
		return LEGACY_BLOCK_MODE_LABELS[value] ?? value;
	}
	if (typeof value === "string") return `"${truncateText(value)}"`;
	if (typeof value === "number") {
		return key === "plannedDurationDays" ? `${value} days` : String(value);
	}
	if (typeof value === "boolean") return value ? "yes" : "no";
	return null;
}

// Hoists `details` one level so criterion snapshots diff like flat objects.
function flattenSnapshot(snapshot: unknown): Record<string, unknown> {
	if (!snapshot || typeof snapshot !== "object") return {};
	const out: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(snapshot as Record<string, unknown>)) {
		if (key === "details" && value && typeof value === "object") {
			Object.assign(out, value);
		} else {
			out[key] = value;
		}
	}
	return out;
}

// Human-readable payload summary for the event log: "field: old → new" lines for updates,
// a few key facts for creates. Replaces the raw-JSON details collapse. `resolveMetricLabel`
// maps stored metric identifiers (e.g. "gps-1") back to display names.
export function describeRtpEventChanges(
	event: RtpEvent,
	resolveMetricLabel?: (key: string) => string | undefined
): string[] {
	const metricName = (key: unknown): unknown =>
		typeof key === "string" ? (resolveMetricLabel?.(key) ?? key) : key;
	const { before, after } = event.payload;
	const lines: string[] = [];
	const push = (line: string) => {
		if (lines.length < 4) lines.push(line);
	};
	const b = flattenSnapshot(before);
	const a = flattenSnapshot(after);

	if (before != null && after != null) {
		for (const key of Object.keys(RTP_CHANGE_LABELS)) {
			if (!(key in a) && !(key in b)) continue;
			if (JSON.stringify(b[key]) === JSON.stringify(a[key])) continue;
			const resolve = key === "metricKey" ? metricName : (v: unknown) => v;
			const prev = formatChangeValue(key, resolve(b[key]));
			const next = formatChangeValue(key, resolve(a[key]));
			if (prev == null && next == null) continue;
			push(`${RTP_CHANGE_LABELS[key]}: ${prev ?? "—"} → ${next ?? "—"}`);
		}
		const prevSigners = Array.isArray(b.allowedSigners) ? b.allowedSigners.length : null;
		const nextSigners = Array.isArray(a.allowedSigners) ? a.allowedSigners.length : null;
		if (prevSigners != null && nextSigners != null && prevSigners !== nextSigners) {
			push(`Sign-off staff: ${prevSigners} → ${nextSigners}`);
		}
		return lines;
	}

	if (after != null) {
		if (typeof a.plannedDurationDays === "number") push(`Planned duration: ${a.plannedDurationDays} days`);
		if (typeof a.metricKey === "string" && a.metricKey) {
			const source = typeof a.metricSource === "string" ? `${a.metricSource} · ` : "";
			push(`Target: ${source}${metricName(a.metricKey)} ${a.comparison ?? ""} ${a.threshold ?? ""}`.trim());
		}
		if (typeof a.goal === "string" && a.goal.trim()) push(`Goal: ${truncateText(a.goal)}`);
		if (typeof a.signedOffNotes === "string" && a.signedOffNotes.trim()) {
			push(`Notes: "${truncateText(a.signedOffNotes)}"`);
		}
		if (event.eventType === "plan_started" && typeof a.startDate === "string") {
			push(`Start date: ${dayjs(a.startDate).format("MMM D, YYYY")}`);
		}
	}
	return lines;
}

// --- plan cards ---

// Card order on the plan list: active plans first, then drafts, then closed plans.
export const PLAN_STATUS_ORDER: Record<RtpPlanStatus, number> = { active: 0, draft: 1, completed: 2, cancelled: 3 };

const longDate = (value: string) => dayjs(value).format("MMM D, YYYY");

// The phase a card highlights: the first open phase of an active plan, else -1.
export function currentPhaseIndex(plan: RtpPlanListItem): number {
	return plan.status === "active" ? plan.phases.findIndex((phase) => !phase.completedAt) : -1;
}

const dayCount = (days: number) => `${days} day${days === 1 ? "" : "s"}`;

// "Aug 25, 2026 → Sep 2, 2026 (9 days, projected)". The day count includes both ends. A draft
// plan shows the planned length of all its phases.
export function planCardDates(plan: RtpPlanListItem): string {
	if (!plan.startDate) {
		const planned = plan.phases.reduce((sum, phase) => sum + phase.plannedDurationDays, 0);
		return planned > 0 ? `Not started (${dayCount(planned)} planned)` : "Not started";
	}
	const last = plan.phases[plan.phases.length - 1];
	const range = (end: string, note = "") => {
		const days = dayjs(end).diff(dayjs(plan.startDate), "day") + 1;
		return `${longDate(plan.startDate!)} → ${longDate(end)} (${dayCount(days)}${note})`;
	};
	if (plan.status === "completed" && last?.completedAt) return range(completedDay(last.completedAt));
	if (plan.status === "active" && last?.projectedEndDate) return range(last.projectedEndDate, ", projected");
	return longDate(plan.startDate);
}

export function planCardPhase(plan: RtpPlanListItem): string {
	const count = plan.phases.length;
	const current = currentPhaseIndex(plan);
	const labels: Record<RtpPlanStatus, string> = {
		draft: `Not started · ${count} phase${count === 1 ? "" : "s"}`,
		active: current >= 0 ? `${current + 1} of ${count} · ${plan.phases[current].name}` : `All ${count} complete`,
		completed: `All ${count} complete`,
		cancelled: "Cancelled",
	};
	return count === 0 ? "No phases" : labels[plan.status];
}

export function planCardInjury(plan: RtpPlanListItem): string {
	return plan.injury ? `${plan.injury.injuryType} · ${plan.injury.injuryLocation}` : "No linked injury";
}

// The date range and length of one phase, for the plan card's phase popover:
// "Aug 31 – Oct 8, 2026 (39 days)". A phase that is not completed ends on its projected end.
// An undated phase (draft plan) shows only its planned length.
export function phaseCardDetails(phase: RtpPhaseDates): { range: string; days: number } {
	const days = phaseDurationDays(phase);
	const length = `(${days} day${days === 1 ? "" : "s"})`;
	const endValue = phase.completedAt ? completedDay(phase.completedAt) : phase.projectedEndDate;
	if (!phase.startDate || !endValue) return { range: `Not started ${length}`, days };
	const start = dayjs(phase.startDate);
	const end = dayjs(endValue);
	const startLabel = start.format(start.year() === end.year() ? "MMM D" : "MMM D, YYYY");
	const range = start.isSame(end, "day") ? longDate(endValue) : `${startLabel} – ${longDate(endValue)}`;
	return { range: `${range} ${length}`, days };
}

// The share of an open phase that has passed by today, from 0 to 1, for the plan card's bar.
export function phaseElapsedShare(phase: RtpPhaseDates): number {
	if (!phase.startDate) return 0;
	const elapsed = dayjs().startOf("day").diff(dayjs(phase.startDate), "day") + 1;
	return Math.min(Math.max(elapsed / phaseDurationDays(phase), 0), 1);
}

// The plan's staff as short names, "B. Bowman", for the plan card.
export function planCardStaff(plan: RtpPlanListItem): string {
	return plan.staff
		.map(({ firstName, lastName }) => [firstName ? `${firstName[0]}.` : "", lastName].filter(Boolean).join(" "))
		.join(", ");
}

